import { useEffect, useMemo, useState } from 'react'
import {
  loadReportPayloadData,
  type ReportPayloadData,
} from '@services/reports/reportPayloadData'

type State = {
  data: ReportPayloadData
  loading: boolean
  error: Error | null
}

export function useReportPayloadData({
  enabled,
  clinicId,
  filters,
  metrics,
  sourceMetricsSchema,
}: {
  enabled: boolean
  clinicId?: string | null
  filters: Record<string, string> | null
  metrics: Record<string, boolean> | null
  sourceMetricsSchema: any[]
}): State {
  const requestKey = useMemo(
    () =>
      JSON.stringify({
        enabled,
        clinicId,
        filters,
        metrics,
        schemaReady: sourceMetricsSchema.length > 0,
      }),
    [enabled, clinicId, filters, metrics, sourceMetricsSchema.length],
  )
  const [state, setState] = useState<State>({
    data: {},
    loading: false,
    error: null,
  })

  useEffect(() => {
    if (
      !enabled ||
      !clinicId ||
      !filters ||
      !metrics ||
      sourceMetricsSchema.length === 0
    ) {
      setState({ data: {}, loading: false, error: null })
      return
    }

    let cancelled = false
    setState((current) => ({ ...current, loading: true, error: null }))
    void loadReportPayloadData({
      clinicId,
      filters,
      metrics,
      sourceMetricsSchema,
      onProgress: (data) => {
        if (!cancelled) {
          setState((current) => ({ ...current, data }))
        }
      },
    })
      .then((data) => {
        if (!cancelled) setState({ data, loading: false, error: null })
      })
      .catch((reason: unknown) => {
        if (cancelled) return
        const error =
          reason instanceof Error ? reason : new Error(String(reason))
        setState((current) => ({ ...current, loading: false, error }))
      })
    return () => {
      cancelled = true
    }
    // requestKey is the stable semantic dependency for record inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestKey])

  return state
}
